import React, { useEffect, useState } from 'react';
import type { DailyNoteRecord } from '../api';
import { formatDisplayDate } from '../utils/date';

interface Props {
  date: string;
  note: DailyNoteRecord | null;
  title?: string;
  helperText?: string;
  onSave: (date: string, updates: { title: string; content: string }) => Promise<void>;
}

export function DailyNoteEditor({ date, note, title, helperText, onSave }: Props) {
  const [draftTitle, setDraftTitle] = useState(note?.title ?? '');
  const [draftContent, setDraftContent] = useState(note?.content ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftTitle(note?.title ?? '');
    setDraftContent(note?.content ?? '');
  }, [date, note?.content, note?.id, note?.title]);

  const dirty = draftTitle !== (note?.title ?? '') || draftContent !== (note?.content ?? '');

  const handleSave = async () => {
    if (!dirty) {
      return;
    }

    setSaving(true);
    try {
      await onSave(date, { title: draftTitle, content: draftContent });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>{title ?? formatDisplayDate(date, { weekday: 'long', month: 'long', day: 'numeric' })}</h2>
          {helperText && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{helperText}</p>
          )}
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving...' : dirty ? 'Save Note' : 'Saved'}
        </button>
      </div>

      <div className="form-group">
        <label htmlFor={`note-title-${date}`}>Title</label>
        <input
          id={`note-title-${date}`}
          className="form-input"
          placeholder="Optional note title"
          value={draftTitle}
          onChange={(event) => setDraftTitle(event.target.value)}
        />
      </div>

      <div className="form-group" style={{ marginBottom: 0 }}>
        <label htmlFor={`note-content-${date}`}>Daily log</label>
        <textarea
          id={`note-content-${date}`}
          className="form-input"
          rows={10}
          placeholder="Capture what happened, what matters, and what to follow up on..."
          style={{ resize: 'vertical', fontFamily: 'var(--font)', minHeight: 220 }}
          value={draftContent}
          onChange={(event) => setDraftContent(event.target.value)}
        />
      </div>
    </section>
  );
}
